import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import {
  Activity,
  AlertTriangle,
  BarChart3,
  Brain,
  ChevronDown,
  ChevronRight,
  FileWarning,
  Globe,
  Lightbulb,
  MessageSquareWarning,
  Phone,
  PhoneForwarded,
  RefreshCw,
  TrendingDown,
  TrendingUp,
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Progress } from '../../components/ui/progress';
import { Skeleton } from '../../components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../../components/ui/collapsible';
import { Alert, AlertDescription, AlertTitle } from '../../components/ui/alert';
import { toast } from 'sonner';
import api from '../../lib/api';

// Objection category display names
const OBJECTION_LABELS = {
  pricing: 'Pricing',
  trust: 'Trust',
  timing: 'Timing',
  feature_gaps: 'Feature Gaps',
  policy_concerns: 'Policy Concerns',
};

// Language display names
const LANGUAGE_LABELS = {
  en: 'English',
  hi: 'Hindi',
  es: 'Spanish',
};

// Severity badge variants
const SEVERITY_VARIANTS = {
  low: 'outline',
  medium: 'secondary',
  high: 'destructive',
};

export function VoiceAIAnalytics() {
  const { isAuthenticated, loading: authLoading } = useAuth();
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [timeRange, setTimeRange] = useState('30');
  
  // Analytics data
  const [overview, setOverview] = useState(null);
  const [objections, setObjections] = useState(null);
  const [insights, setInsights] = useState([]);
  const [expandedInsights, setExpandedInsights] = useState({});

  // Get token from localStorage
  const getToken = () => localStorage.getItem('access_token');

  const fetchAnalytics = useCallback(async () => {
    const token = getToken();
    if (!token) {
      setLoading(false);
      return;
    }
    
    setLoading(true);
    try {
      const days = parseInt(timeRange);
      
      const [overviewRes, objectionsRes, insightsRes] = await Promise.all([
        api.get(`/api/voice/analytics/overview?days=${days}`).catch(e => ({ data: null, error: e })),
        api.get(`/api/voice/analytics/objections?days=${days}`).catch(e => ({ data: null, error: e })),
        api.get('/api/voice/analytics/insights').catch(e => ({ data: null, error: e })),
      ]);

      let hasData = false;
      if (overviewRes.data) {
        setOverview(overviewRes.data);
        hasData = true;
      }
      if (objectionsRes.data) {
        setObjections(objectionsRes.data);
        hasData = true;
      }
      if (insightsRes.data) {
        setInsights(Array.isArray(insightsRes.data) ? insightsRes.data : (insightsRes.data.insights || []));
        hasData = true;
      }
      
      if (!hasData) {
        toast.error('Failed to load analytics data');
      }
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
      toast.error('Failed to load analytics. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [timeRange]);

  useEffect(() => {
    // Wait for auth to finish loading and user to be authenticated
    if (!authLoading && isAuthenticated) {
      fetchAnalytics();
    } else if (!authLoading && !isAuthenticated) {
      // Auth finished loading but user is not authenticated
      setLoading(false);
    }
  }, [fetchAnalytics, isAuthenticated, authLoading]);

  const handleGenerateAnalytics = async () => {
    setGenerating(true);
    try {
      const response = await api.post('/api/voice/analytics/generate', {
        days: parseInt(timeRange),
        include_insights: true,
      });
      toast.success(`Generated analytics for ${response.data.calls_processed} calls, ${response.data.insights_generated} insights`);
      fetchAnalytics();
    } catch (error) {
      console.error('Failed to generate analytics:', error);
      const detail = error.response?.data?.detail;
      toast.error((typeof detail === 'string' ? detail : detail?.message) || 'Failed to generate analytics');
    } finally {
      setGenerating(false);
    }
  };

  const toggleInsight = (id) => {
    setExpandedInsights((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  // Show loading skeleton while auth is loading or data is being fetched
  if (authLoading || (loading && isAuthenticated)) {
    return (
      <div className="space-y-6" data-testid="analytics-loading">
        <Skeleton className="h-8 w-64" />
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} className="h-32" />
          ))}
        </div>
        <Skeleton className="h-96" />
      </div>
    );
  }

  // Show message if not authenticated
  if (!isAuthenticated) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <h2 className="text-xl font-semibold mb-2">Authentication Required</h2>
        <p className="text-muted-foreground">Please log in to view Voice AI Analytics</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="voice-ai-analytics">
      {/* Page Header */}
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Voice AI Analytics</h1>
          <p className="text-muted-foreground">
            Post-call intelligence for call quality, objections, and coaching insights
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={timeRange} onValueChange={setTimeRange} data-testid="time-range-selector">
            <SelectTrigger className="w-[140px]">
              <SelectValue placeholder="Time range" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">Last 7 days</SelectItem>
              <SelectItem value="30">Last 30 days</SelectItem>
              <SelectItem value="90">Last 90 days</SelectItem>
            </SelectContent>
          </Select>
          <Button
            variant="outline"
            size="sm"
            onClick={handleGenerateAnalytics}
            disabled={generating}
            data-testid="refresh-analytics-btn"
          >
            <RefreshCw className={`mr-2 h-4 w-4 ${generating ? 'animate-spin' : ''}`} />
            {generating ? 'Refreshing analytics...' : 'Refresh Analytics'}
          </Button>
        </div>
      </div>

      {/* Demo Banner */}
      {overview?.is_demo && (
        <Alert variant="warning" className="border-amber-200 bg-amber-50 dark:border-amber-900 dark:bg-amber-950">
          <AlertTriangle className="h-4 w-4 text-amber-600" />
          <AlertTitle className="text-amber-800 dark:text-amber-200">Demo Mode</AlertTitle>
          <AlertDescription className="text-amber-700 dark:text-amber-300">
            You are viewing demo analytics data. This data is for demonstration purposes only.
          </AlertDescription>
        </Alert>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="grid w-full grid-cols-3 lg:w-auto lg:grid-cols-none lg:inline-flex">
          <TabsTrigger value="overview" className="gap-2" data-testid="tab-overview">
            <BarChart3 className="h-4 w-4" />
            Overview
          </TabsTrigger>
          <TabsTrigger value="objections" className="gap-2" data-testid="tab-objections">
            <MessageSquareWarning className="h-4 w-4" />
            Objections
          </TabsTrigger>
          <TabsTrigger value="insights" className="gap-2" data-testid="tab-insights">
            <Lightbulb className="h-4 w-4" />
            Coaching Insights
          </TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          {/* Stats Grid */}
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            <Card data-testid="stat-total-calls">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium">Total Calls</CardTitle>
                <Phone className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{overview?.total_calls || 0}</div>
                <p className="text-xs text-muted-foreground">
                  {overview?.completed_calls || 0} completed, {overview?.dropped_calls || 0} dropped
                </p>
              </CardContent>
            </Card>

            <Card data-testid="stat-completion-rate">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium">Completion Rate</CardTitle>
                <Activity className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{overview?.completion_rate || 0}%</div>
                <Progress value={overview?.completion_rate || 0} className="mt-2 h-2" />
              </CardContent>
            </Card>

            <Card data-testid="stat-quality-score">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium">Avg Quality Score</CardTitle>
                <Brain className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {((overview?.avg_quality_score || 0) * 100).toFixed(0)}%
                </div>
                <Progress
                  value={(overview?.avg_quality_score || 0) * 100}
                  className="mt-2 h-2"
                />
              </CardContent>
            </Card>

            <Card data-testid="stat-transfer-rate">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium">Human Transfer Rate</CardTitle>
                <PhoneForwarded className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{overview?.human_transfer_rate || 0}%</div>
                <Progress value={overview?.human_transfer_rate || 0} className="mt-2 h-2" />
              </CardContent>
            </Card>
          </div>

          {/* Additional Metrics */}
          <div className="grid gap-6 lg:grid-cols-2">
            {/* Language Distribution */}
            <Card data-testid="language-distribution">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Globe className="h-5 w-5" />
                  Language Distribution
                </CardTitle>
                <CardDescription>Calls by detected language</CardDescription>
              </CardHeader>
              <CardContent>
                {overview?.language_breakdown &&
                Object.keys(overview.language_breakdown).length > 0 ? (
                  <div className="space-y-4">
                    {Object.entries(overview.language_breakdown).map(([lang, count]) => {
                      const percentage =
                        overview.total_calls > 0
                          ? ((count / overview.total_calls) * 100).toFixed(1)
                          : 0;
                      return (
                        <div key={lang}>
                          <div className="mb-1 flex justify-between text-sm">
                            <span>{LANGUAGE_LABELS[lang] || lang.toUpperCase()}</span>
                            <span className="font-medium">
                              {count} ({percentage}%)
                            </span>
                          </div>
                          <Progress value={parseFloat(percentage)} className="h-2" />
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-center text-muted-foreground py-8">
                    No language data available
                  </p>
                )}
              </CardContent>
            </Card>

            {/* KB Coverage & Confidence */}
            <Card data-testid="kb-confidence">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="h-5 w-5" />
                  Performance Metrics
                </CardTitle>
                <CardDescription>KB coverage and confidence trends</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <div className="mb-1 flex justify-between text-sm">
                    <span>KB Coverage Rate</span>
                    <span className="font-medium">{overview?.kb_coverage_rate || 0}%</span>
                  </div>
                  <Progress value={overview?.kb_coverage_rate || 0} className="h-2" />
                  <p className="mt-1 text-xs text-muted-foreground">
                    Percentage of queries answered by Knowledge Base
                  </p>
                </div>
                <div>
                  <div className="mb-1 flex justify-between text-sm">
                    <span>Avg Confidence Score</span>
                    <span className="font-medium">
                      {((overview?.avg_confidence_score || 0) * 100).toFixed(0)}%
                    </span>
                  </div>
                  <Progress
                    value={(overview?.avg_confidence_score || 0) * 100}
                    className="h-2"
                  />
                  <p className="mt-1 text-xs text-muted-foreground">
                    AI confidence in call handling
                  </p>
                </div>
                <div>
                  <div className="mb-1 flex justify-between text-sm">
                    <span>Avg Call Duration</span>
                    <span className="font-medium">
                      {Math.floor((overview?.avg_duration || 0) / 60)}m{' '}
                      {Math.round((overview?.avg_duration || 0) % 60)}s
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Objections Tab */}
        <TabsContent value="objections" className="space-y-6">
          {/* Objection Summary */}
          <div className="grid gap-4 md:grid-cols-3">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium">Calls Analyzed</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{objections?.total_calls_analyzed || 0}</div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium">Calls with Objections</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{objections?.calls_with_objections || 0}</div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium">Objection Rate</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{objections?.objection_rate || 0}%</div>
              </CardContent>
            </Card>
          </div>

          {/* Top Objections */}
          <Card data-testid="top-objections">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MessageSquareWarning className="h-5 w-5" />
                Top Objections
              </CardTitle>
              <CardDescription>Most common objection categories detected in calls</CardDescription>
            </CardHeader>
            <CardContent>
              {objections?.top_objections && objections.top_objections.length > 0 ? (
                <div className="space-y-4">
                  {objections.top_objections.map((obj, index) => (
                    <div
                      key={obj.category}
                      className="flex items-center justify-between rounded-lg border p-3"
                    >
                      <div className="flex items-center gap-3">
                        <span className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10 text-sm font-medium">
                          {index + 1}
                        </span>
                        <div>
                          <p className="font-medium">
                            {OBJECTION_LABELS[obj.category] || obj.category}
                          </p>
                          <p className="text-sm text-muted-foreground">
                            {obj.count} occurrences
                          </p>
                        </div>
                      </div>
                      <Badge variant="secondary">{obj.percentage}%</Badge>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="py-8 text-center text-muted-foreground">
                  No objections detected in the selected period
                </p>
              )}
            </CardContent>
          </Card>

          {/* Objection to Transfer Correlation */}
          <Card data-testid="objection-transfer-correlation">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingDown className="h-5 w-5" />
                Objection → Transfer Correlation
              </CardTitle>
              <CardDescription>
                How often each objection type leads to human transfer
              </CardDescription>
            </CardHeader>
            <CardContent>
              {objections?.objection_transfer_correlation &&
              objections.objection_transfer_correlation.length > 0 ? (
                <div className="space-y-4">
                  {objections.objection_transfer_correlation.map((item) => (
                    <div key={item.category}>
                      <div className="mb-1 flex justify-between text-sm">
                        <span>{OBJECTION_LABELS[item.category] || item.category}</span>
                        <span className="font-medium">
                          {item.resulted_in_transfer} / {item.total_occurrences} ({item.transfer_rate}%)
                        </span>
                      </div>
                      <Progress
                        value={item.transfer_rate}
                        className={`h-2 ${item.transfer_rate > 50 ? '[&>div]:bg-destructive' : ''}`}
                      />
                    </div>
                  ))}
                </div>
              ) : (
                <p className="py-8 text-center text-muted-foreground">
                  No correlation data available
                </p>
              )}
            </CardContent>
          </Card>

          {/* Objections by Language */}
          <Card data-testid="objections-by-language">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Globe className="h-5 w-5" />
                Objections by Language
              </CardTitle>
              <CardDescription>Objection distribution across languages</CardDescription>
            </CardHeader>
            <CardContent>
              {objections?.objection_by_language &&
              Object.keys(objections.objection_by_language).length > 0 ? (
                <div className="grid gap-4 md:grid-cols-3">
                  {Object.entries(objections.objection_by_language).map(([lang, categories]) => (
                    <div key={lang} className="rounded-lg border p-4">
                      <h4 className="mb-3 font-medium">
                        {LANGUAGE_LABELS[lang] || lang.toUpperCase()}
                      </h4>
                      <div className="space-y-2">
                        {Object.entries(categories).map(([cat, count]) => (
                          <div key={cat} className="flex justify-between text-sm">
                            <span className="text-muted-foreground">
                              {OBJECTION_LABELS[cat] || cat}
                            </span>
                            <span className="font-medium">{count}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="py-8 text-center text-muted-foreground">
                  No language-specific objection data available
                </p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Coaching Insights Tab */}
        <TabsContent value="insights" className="space-y-6">
          {insights && insights.length > 0 ? (
            <div className="space-y-4">
              {insights.map((insight) => {
                // Support both old format (insight_type) and new format (category/title)
                const insightType = insight.insight_type || insight.category || 'general';
                const insightTitle = insight.title || (insightType ? insightType.replace(/_/g, ' ').toUpperCase() : 'Insight');
                const insightDescription = insight.description || '';
                
                return (
                <Collapsible
                  key={insight.id}
                  open={expandedInsights[insight.id]}
                  onOpenChange={() => toggleInsight(insight.id)}
                >
                  <Card data-testid={`insight-${insight.id}`}>
                    <CollapsibleTrigger asChild>
                      <CardHeader className="cursor-pointer hover:bg-muted/50">
                        <div className="flex items-start justify-between">
                          <div className="flex items-start gap-3">
                            {expandedInsights[insight.id] ? (
                              <ChevronDown className="mt-1 h-4 w-4" />
                            ) : (
                              <ChevronRight className="mt-1 h-4 w-4" />
                            )}
                            <div>
                              <CardTitle className="text-base flex items-center gap-2">
                                {(insightType === 'quality' || insightType === 'performance') && (
                                  <Activity className="h-4 w-4 text-blue-500" />
                                )}
                                {insightType === 'objection' && (
                                  <MessageSquareWarning className="h-4 w-4 text-amber-500" />
                                )}
                                {(insightType === 'kb_gap' || insightType === 'strategy') && (
                                  <FileWarning className="h-4 w-4 text-teal-500" />
                                )}
                                {insightType === 'language' && (
                                  <Globe className="h-4 w-4 text-teal-500" />
                                )}
                                {insightTitle}
                              </CardTitle>
                              <CardDescription className="mt-1">
                                {insightDescription}
                              </CardDescription>
                            </div>
                          </div>
                          <Badge variant={SEVERITY_VARIANTS[insight.severity] || 'secondary'}>
                            {insight.severity}
                          </Badge>
                        </div>
                      </CardHeader>
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <CardContent className="pt-0">
                        <div className="rounded-lg bg-muted/50 p-4 space-y-3">
                          {/* Recommendation (new format) */}
                          {insight.recommendation && (
                            <div>
                              <h5 className="mb-2 text-sm font-medium">Recommendation</h5>
                              <p className="text-sm text-muted-foreground">{insight.recommendation}</p>
                            </div>
                          )}
                          
                          {/* Metrics (old format) */}
                          {insight.metrics && Object.keys(insight.metrics).length > 0 && (
                            <div>
                              <h5 className="mb-2 text-sm font-medium">Metrics</h5>
                              <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                                {Object.entries(insight.metrics).map(([key, value]) => (
                                  <div
                                    key={key}
                                    className="flex justify-between rounded border bg-background p-2 text-sm"
                                  >
                                    <span className="text-muted-foreground">
                                      {key.replace(/_/g, ' ')}
                                    </span>
                                    <span className="font-medium">
                                      {typeof value === 'number' && value < 1 && value > 0
                                        ? `${(value * 100).toFixed(0)}%`
                                        : value}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                          
                          {/* Related Calls (old format) */}
                          {insight.related_calls && insight.related_calls.length > 0 && (
                            <div>
                              <h5 className="mb-2 text-sm font-medium">
                                Related Calls ({insight.related_calls.length})
                              </h5>
                              <div className="flex flex-wrap gap-2">
                                {insight.related_calls.slice(0, 5).map((callId) => (
                                  <Badge key={callId} variant="outline" className="font-mono text-xs">
                                    {callId.slice(0, 8)}...
                                  </Badge>
                                ))}
                                {insight.related_calls.length > 5 && (
                                  <Badge variant="outline">
                                    +{insight.related_calls.length - 5} more
                                  </Badge>
                                )}
                              </div>
                            </div>
                          )}
                          
                          {/* Created At (only if available) */}
                          {insight.created_at && (
                            <p className="text-xs text-muted-foreground">
                              Generated: {new Date(insight.created_at).toLocaleString()}
                            </p>
                          )}
                        </div>
                      </CardContent>
                    </CollapsibleContent>
                  </Card>
                </Collapsible>
              )})}
            </div>
          ) : (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-12">
                <Lightbulb className="mb-4 h-12 w-12 text-muted-foreground" />
                <h3 className="mb-2 text-lg font-medium">No Coaching Insights Available</h3>
                <p className="mb-4 text-center text-muted-foreground">
                  Insights are generated from call analytics data.
                  <br />
                  Click &ldquo;Refresh Analytics&rdquo; to generate insights from recent calls.
                </p>
                <Button onClick={handleGenerateAnalytics} disabled={generating}>
                  <RefreshCw className={`mr-2 h-4 w-4 ${generating ? 'animate-spin' : ''}`} />
                  Generate Insights
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Insights Legend */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Understanding Insights</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                <div className="flex items-start gap-2">
                  <Activity className="mt-0.5 h-4 w-4 text-blue-500" />
                  <div>
                    <p className="font-medium text-sm">Quality</p>
                    <p className="text-xs text-muted-foreground">
                      Overall call quality observations
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <MessageSquareWarning className="mt-0.5 h-4 w-4 text-amber-500" />
                  <div>
                    <p className="font-medium text-sm">Objection</p>
                    <p className="text-xs text-muted-foreground">
                      Objection pattern insights
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <FileWarning className="mt-0.5 h-4 w-4 text-red-500" />
                  <div>
                    <p className="font-medium text-sm">KB Gap</p>
                    <p className="text-xs text-muted-foreground">
                      Knowledge Base coverage gaps
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <Globe className="mt-0.5 h-4 w-4 text-teal-500" />
                  <div>
                    <p className="font-medium text-sm">Language</p>
                    <p className="text-xs text-muted-foreground">
                      Language-specific insights
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

export default VoiceAIAnalytics;
